import React, { useState, useEffect } from 'react';
import { 
  Search, Mail, Crown, Shield, Ban, Calendar, Lock, Trash2, ChevronDown,
  Loader2, Zap, ShieldCheck, ShieldAlert, UserCheck, Eye, EyeOff, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const USERS_PER_PAGE = 20;

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const d = typeof dateStr === 'string' && !dateStr.endsWith('Z') && !dateStr.includes('+') ? dateStr + 'Z' : dateStr;
  return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const AdminUsersTab = ({ token, toast, currentUserId }) => {
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [usersPage, setUsersPage] = useState(1);
  const [actionLoading, setActionLoading] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const res = await axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } });
      setUsers(res.data.users || []);
    } catch (e) {
      console.error('Error loading users:', e);
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => { loadUsers(); }, []);

  useEffect(() => { setUsersPage(1); }, [searchQuery, filterRole]);

  const filteredUsers = users.filter(u => {
    const matchesSearch = u.email.toLowerCase().includes(searchQuery.toLowerCase()) || u.name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filterRole === 'all' || (filterRole === 'admin' && u.is_admin) || (filterRole === 'pro' && u.is_pro) || (filterRole === 'suspended' && u.is_suspended) || (filterRole === 'free' && !u.is_pro && !u.is_admin);
    return matchesSearch && matchesFilter;
  });

  const totalUsersPages = Math.ceil(filteredUsers.length / USERS_PER_PAGE);
  const paginatedUsers = filteredUsers.slice((usersPage - 1) * USERS_PER_PAGE, usersPage * USERS_PER_PAGE);

  const handleUserAction = async (userId, action) => {
    setActionLoading(userId);
    try {
      let endpoint = '';
      let method = 'put';
      switch (action) {
        case 'suspend': endpoint = `/admin/users/${userId}/suspend`; break;
        case 'unsuspend': endpoint = `/admin/users/${userId}/unsuspend`; break;
        case 'promote': endpoint = `/admin/users/${userId}/promote`; break;
        case 'demote': endpoint = `/admin/users/${userId}/demote`; break;
        case 'toggle-pro': endpoint = `/admin/users/${userId}/toggle-pro`; break;
        case 'toggle-ultimate': endpoint = `/admin/users/${userId}/toggle-ultimate`; break;
        case 'reset-2fa':
          endpoint = `/admin/users/${userId}/reset-2fa`;
          if (!window.confirm('Réinitialiser la 2FA de cet utilisateur ? Il devra la reconfigurer à sa prochaine connexion.')) { setActionLoading(null); return; }
          break;
        case 'delete':
          endpoint = `/admin/users/${userId}`; method = 'delete';
          if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ? Cette action est irréversible.')) { setActionLoading(null); return; }
          break;
        default: return;
      }
      const res = method === 'delete'
        ? await axios.delete(`${API}${endpoint}`, { headers: { Authorization: `Bearer ${token}` } })
        : await axios.put(`${API}${endpoint}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Succès', description: res.data.message });
      loadUsers();
    } catch (error) {
      toast({ title: 'Erreur', description: error.response?.data?.detail || 'Une erreur est survenue', variant: 'destructive' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleChangePassword = async () => {
    if (!selectedUser || !newPassword) return;
    if (newPassword.length < 6) { toast({ title: 'Erreur', description: 'Le mot de passe doit contenir au moins 6 caractères', variant: 'destructive' }); return; }
    if (newPassword !== confirmPassword) { toast({ title: 'Erreur', description: 'Les mots de passe ne correspondent pas', variant: 'destructive' }); return; }
    setChangingPassword(true);
    try {
      await axios.put(`${API}/admin/users/${selectedUser.id}/password`, { new_password: newPassword }, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Succès', description: `Mot de passe de ${selectedUser.email} modifié` });
      setShowPasswordModal(false); setSelectedUser(null); setNewPassword(''); setConfirmPassword('');
    } catch (error) {
      toast({ title: 'Erreur', description: error.response?.data?.detail || 'Impossible de modifier le mot de passe', variant: 'destructive' });
    } finally {
      setChangingPassword(false);
    }
  };

  if (loadingUsers) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-blue-400 animate-spin" /></div>;
  }

  return (
    <>
      <div className="space-y-4">
        <div className="flex flex-col md:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-400/50" />
            <input type="text" placeholder="Rechercher par email ou nom..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-full bg-slate-800/50 border border-blue-500/20 rounded-lg pl-10 pr-4 py-2.5 text-white placeholder-blue-300/40 focus:outline-none focus:border-blue-400 text-sm" />
          </div>
          <select value={filterRole} onChange={(e) => setFilterRole(e.target.value)} className="bg-slate-800/50 border border-blue-500/20 rounded-lg px-4 py-2.5 text-blue-300 text-sm focus:outline-none focus:border-blue-400 cursor-pointer">
            <option value="all">Tous les utilisateurs</option>
            <option value="admin">Administrateurs</option>
            <option value="pro">Abonnés Pro</option>
            <option value="free">Gratuits</option>
            <option value="suspended">Suspendus</option>
          </select>
        </div>

        <div className="text-sm text-blue-300/60 mb-2">{filteredUsers.length} utilisateur{filteredUsers.length !== 1 ? 's' : ''}</div>

        <div className="bg-slate-800/50 border border-blue-500/20 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-blue-500/10">
                  <th className="text-left px-6 py-4 text-xs font-medium text-blue-300/70 uppercase tracking-wider">Utilisateur</th>
                  <th className="text-left px-6 py-4 text-xs font-medium text-blue-300/70 uppercase tracking-wider">Statut</th>
                  <th className="text-left px-6 py-4 text-xs font-medium text-blue-300/70 uppercase tracking-wider">Partages</th>
                  <th className="text-left px-6 py-4 text-xs font-medium text-blue-300/70 uppercase tracking-wider">Inscription</th>
                  <th className="text-right px-6 py-4 text-xs font-medium text-blue-300/70 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-blue-500/10">
                {paginatedUsers.map((u) => (
                  <tr key={u.id} className={`hover:bg-slate-800/50 ${u.is_suspended ? 'opacity-60' : ''}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold ${u.is_admin ? 'bg-red-500' : u.is_pro ? 'bg-yellow-500' : 'bg-blue-500'}`}>
                          {u.name?.charAt(0)?.toUpperCase() || u.email.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-white">{u.name || 'Sans nom'}</p>
                          <p className="text-sm text-blue-300/60 flex items-center gap-1"><Mail className="w-3 h-3" />{u.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {u.is_admin && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 text-xs"><Shield className="w-3 h-3" /> Admin</span>}
                        {u.is_pro && !u.is_ultimate && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-400 text-xs"><Crown className="w-3 h-3" /> Pro</span>}
                        {u.is_ultimate && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-400 text-xs"><Zap className="w-3 h-3" /> Ultimate</span>}
                        {u.is_suspended && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 text-xs"><Ban className="w-3 h-3" /> Suspendu</span>}
                        {u.two_factor_enabled && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 text-xs" data-testid={`user-2fa-badge-${u.id}`}><ShieldCheck className="w-3 h-3" /> 2FA</span>}
                        {u.is_admin && !u.two_factor_enabled && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-500/20 text-orange-400 text-xs animate-pulse"><ShieldAlert className="w-3 h-3" /> 2FA requis</span>}
                        {!u.is_admin && !u.is_pro && !u.is_suspended && <span className="px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-300 text-xs">Gratuit</span>}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-blue-300">{u.share_count || 0}</td>
                    <td className="px-6 py-4 text-sm text-blue-300/60"><span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDate(u.created_at)}</span></td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <div className="relative group">
                          <Button variant="ghost" size="sm" className="text-blue-300 hover:bg-blue-500/20" disabled={actionLoading === u.id}>
                            {actionLoading === u.id ? <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" /> : <>Actions<ChevronDown className="w-4 h-4 ml-1" /></>}
                          </Button>
                          <div className="absolute right-0 top-full mt-1 w-48 bg-slate-800 border border-blue-500/20 rounded-xl shadow-xl py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                            <button onClick={() => handleUserAction(u.id, 'toggle-pro')} className="w-full px-4 py-2 text-left text-sm text-blue-200 hover:bg-blue-500/10 flex items-center gap-2"><Crown className="w-4 h-4" />{u.is_pro ? 'Retirer Pro' : 'Passer Pro'}</button>
                            <button onClick={() => handleUserAction(u.id, 'toggle-ultimate')} className="w-full px-4 py-2 text-left text-sm text-purple-200 hover:bg-purple-500/10 flex items-center gap-2"><Zap className="w-4 h-4" />{u.is_ultimate ? 'Retirer Ultimate' : 'Passer Ultimate'}</button>
                            {!u.is_admin && <button onClick={() => handleUserAction(u.id, u.is_suspended ? 'unsuspend' : 'suspend')} className="w-full px-4 py-2 text-left text-sm text-blue-200 hover:bg-blue-500/10 flex items-center gap-2">{u.is_suspended ? <><UserCheck className="w-4 h-4" /> Réactiver</> : <><Ban className="w-4 h-4" /> Suspendre</>}</button>}
                            {u.id !== currentUserId && <button onClick={() => handleUserAction(u.id, u.is_admin ? 'demote' : 'promote')} className="w-full px-4 py-2 text-left text-sm text-blue-200 hover:bg-blue-500/10 flex items-center gap-2"><Shield className="w-4 h-4" />{u.is_admin ? 'Retirer Admin' : 'Promouvoir Admin'}</button>}
                            {u.id !== currentUserId && <button onClick={() => { setSelectedUser(u); setShowPasswordModal(true); }} className="w-full px-4 py-2 text-left text-sm text-blue-200 hover:bg-blue-500/10 flex items-center gap-2"><Lock className="w-4 h-4" /> Modifier mot de passe</button>}
                            {u.two_factor_enabled && <button onClick={() => handleUserAction(u.id, 'reset-2fa')} className="w-full px-4 py-2 text-left text-sm text-orange-300 hover:bg-orange-500/10 flex items-center gap-2" data-testid={`reset-2fa-btn-${u.id}`}><ShieldAlert className="w-4 h-4" /> Réinitialiser 2FA</button>}
                            {u.id !== currentUserId && <><div className="border-t border-blue-500/10 my-1"></div><button onClick={() => handleUserAction(u.id, 'delete')} className="w-full px-4 py-2 text-left text-sm text-red-400 hover:bg-red-500/10 flex items-center gap-2"><Trash2 className="w-4 h-4" /> Supprimer</button></>}
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
                {paginatedUsers.length === 0 && <tr><td colSpan={5} className="px-6 py-12 text-center text-blue-300/50">Aucun utilisateur trouvé</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        {totalUsersPages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-blue-300/60">Page {usersPage} sur {totalUsersPages} ({filteredUsers.length} utilisateur{filteredUsers.length !== 1 ? 's' : ''})</p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setUsersPage(1)} disabled={usersPage === 1} className="border-blue-500/30 text-blue-300 disabled:opacity-30">««</Button>
              <Button variant="outline" size="sm" onClick={() => setUsersPage(p => Math.max(1, p - 1))} disabled={usersPage === 1} className="border-blue-500/30 text-blue-300 disabled:opacity-30">Précédent</Button>
              <div className="flex items-center gap-1">
                {[...Array(Math.min(5, totalUsersPages))].map((_, i) => {
                  let pageNum;
                  if (totalUsersPages <= 5) pageNum = i + 1;
                  else if (usersPage <= 3) pageNum = i + 1;
                  else if (usersPage >= totalUsersPages - 2) pageNum = totalUsersPages - 4 + i;
                  else pageNum = usersPage - 2 + i;
                  return <Button key={pageNum} variant={usersPage === pageNum ? "default" : "outline"} size="sm" onClick={() => setUsersPage(pageNum)} className={usersPage === pageNum ? "bg-blue-600 text-white" : "border-blue-500/30 text-blue-300"}>{pageNum}</Button>;
                })}
              </div>
              <Button variant="outline" size="sm" onClick={() => setUsersPage(p => Math.min(totalUsersPages, p + 1))} disabled={usersPage === totalUsersPages} className="border-blue-500/30 text-blue-300 disabled:opacity-30">Suivant</Button>
              <Button variant="outline" size="sm" onClick={() => setUsersPage(totalUsersPages)} disabled={usersPage === totalUsersPages} className="border-blue-500/30 text-blue-300 disabled:opacity-30">»»</Button>
            </div>
          </div>
        )}
      </div>

      {showPasswordModal && selectedUser && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 border border-blue-500/30 rounded-2xl shadow-2xl max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b border-blue-500/20">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2"><Lock className="w-5 h-5 text-blue-400" />Modifier le mot de passe</h2>
              <button onClick={() => { setShowPasswordModal(false); setSelectedUser(null); setNewPassword(''); setConfirmPassword(''); }} className="text-blue-300/60 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-blue-300/70 text-sm">Modifier le mot de passe de <span className="text-white font-medium">{selectedUser.email}</span></p>
              <div>
                <label className="block text-sm text-blue-300/70 mb-1.5">Nouveau mot de passe</label>
                <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="••••••••" className="w-full bg-slate-800/50 border border-blue-500/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/30 focus:outline-none focus:border-blue-400" />
                <p className="text-xs text-blue-300/50 mt-1">Minimum 6 caractères</p>
              </div>
              <div>
                <label className="block text-sm text-blue-300/70 mb-1.5">Confirmer le mot de passe</label>
                <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="••••••••" className="w-full bg-slate-800/50 border border-blue-500/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/30 focus:outline-none focus:border-blue-400" />
              </div>
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => { setShowPasswordModal(false); setSelectedUser(null); setNewPassword(''); setConfirmPassword(''); }} className="flex-1 border-blue-500/30 text-blue-300">Annuler</Button>
                <Button onClick={handleChangePassword} disabled={changingPassword || !newPassword || !confirmPassword} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white">{changingPassword ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Modifier'}</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminUsersTab;
